import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useCriterias } from 'services/Data/CriteriaServices';
import { useCreateSubmission, useSubmissionsByDealer } from 'services/Data/SubmissionServices';
import { useQuarters } from 'services/Data/QuarterServices';
import { useDealer } from 'services/Data/DealerServices';
import { CertificationStatusEnumValue } from 'enums/CertificationStatusEnum';
import { translateLabel } from 'services/Utilities/labelUtils';
import {
  getDealerCountryId,
  getEligibleCriteriasForDealer,
  isCriteriaOfCertificationType,
  normalizeCollection,
} from 'services/Utilities/certificationCriteriaUtils';

const CreateSubmissionDialog = ({ open, onClose, initialCriteriaId = null, criteriaCertificationType = null }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const account = useSelector(state => state.AccountReducer);

  const dealerId = account?.profile?.dealer_Id;
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const { data: criterias, isLoading: isLoadingCriterias, error: criteriasError } = useCriterias();
  const { data: dealerSubmissions, isLoading: isLoadingDealerSubmissions, error: submissionsError } = useSubmissionsByDealer(
    dealerId,
  );
  const { data: dealerDetails, isLoading: isLoadingDealerDetails, error: dealerDetailsError } = useDealer(dealerId, {
    enabled: open && !!dealerId,
  });
  const { data: quartersData, isLoading: isLoadingQuarters, error: quartersError } = useQuarters(
    { currentPage: 0, pageSize: 0 },
    { enabled: open },
  );
  const createSubmissionMutation = useCreateSubmission();
  const normalizedSubmissions = useMemo(() => normalizeCollection(dealerSubmissions), [dealerSubmissions]);

  const normalizedCriterias = useMemo(() => normalizeCollection(criterias), [criterias]);

  const dealerCountryId = useMemo(() => getDealerCountryId(dealerDetails), [dealerDetails]);

  const eligibleCriterias = useMemo(
    () =>
      getEligibleCriteriasForDealer({
        criterias: normalizedCriterias,
        dealerId,
        dealerDetails,
      }),
    [normalizedCriterias, dealerId, dealerDetails],
  );

  const filteredCriterias = useMemo(() => {
    if (criteriaCertificationType === null || criteriaCertificationType === undefined) {
      return eligibleCriterias;
    }

    return eligibleCriterias.filter(criteria => isCriteriaOfCertificationType(criteria, criteriaCertificationType));
  }, [eligibleCriterias, criteriaCertificationType]);

  const missingDealerCountry = useMemo(() => !dealerCountryId && !!dealerDetails && !isLoadingDealerDetails, [
    dealerCountryId,
    dealerDetails,
    isLoadingDealerDetails,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (initialCriteriaId) {
      const initialCriteria = filteredCriterias.find(criteria => criteria.id === initialCriteriaId) ?? null;
      setSelectedCriteria(initialCriteria);
      return;
    }

    if (!selectedCriteria) {
      return;
    }

    const stillVisible = filteredCriterias.some(criteria => criteria.id === selectedCriteria.id);
    if (!stillVisible) {
      setSelectedCriteria(null);
    }
  }, [open, initialCriteriaId, filteredCriterias, selectedCriteria]);

  const quarters = useMemo(() => {
    if (!quartersData) {
      return [];
    }

    // API returns either an array or { items, count }
    if (Array.isArray(quartersData)) {
      return quartersData;
    }

    if (Array.isArray(quartersData.items)) {
      return quartersData.items;
    }

    return [];
  }, [quartersData]);

  const currentQuarter = useMemo(() => {
    if (!quarters.length) {
      return null;
    }

    const today = new Date();

    const parseDate = value => {
      if (!value) return null;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    return (
      quarters.find(quarter => {
        const start = parseDate(quarter.startDate ?? quarter.StartDate);
        const end = parseDate(quarter.endDate ?? quarter.EndDate);

        if (!start || !end) {
          return false;
        }

        return today >= start && today <= end;
      }) || null
    );
  }, [quarters]);

  const handleCriteriaSelect = criteria => {
    setSelectedCriteria(criteria);
    setError(null);
  };

  const handleCreate = async () => {
    if (!selectedCriteria) {
      setError(t('submissions.pleaseSelectCriteria'));
      return;
    }

    if (!dealerId) {
      setError(t('submissions.noDealerFound'));
      return;
    }

    setIsCreating(true);
    setError(null);

    if (!currentQuarter) {
      setError(t('submissions.noActiveQuarter'));
      setIsCreating(false);
      return;
    }

    const existingSubmission = normalizedSubmissions.find(
      submission => submission.criteria_Id === selectedCriteria.id && submission.quarter_Id === currentQuarter.id,
    );

    if (existingSubmission) {
      setInfoMessage(t('submissions.redirectingToExisting'));
      setIsCreating(false);
      await new Promise(resolve => setTimeout(resolve, 3200));
      setInfoMessage(null);
      history.push(`/dealer/certification/submissions/${existingSubmission.id}/step/1`);
      onClose();
      return;
    }

    try {
      const newSubmission = {
        id: crypto.randomUUID(),
        name: `${selectedCriteria.name} - ${new Date().toLocaleDateString()}`,
        dealer_Id: dealerId,
        criteria_Id: selectedCriteria.id,
        dateIssued: new Date().toISOString(),
        status: CertificationStatusEnumValue.Pending,
        score: null,
        quarter_Id: currentQuarter.id,
        user_id: account.profile.id,
      };

      const createdSubmission = await createSubmissionMutation.mutateAsync(newSubmission);

      // Navigate to the new submission
      history.push(`/dealer/certification/submissions/${createdSubmission.id}/step/1`);
      onClose();
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      const nextSubmissionQuarterStartDate = err?.response?.data?.nextSubmissionQuarterStartDate;

      if (serverMessage === 'submissions.alreadyCertifiedUntil' && nextSubmissionQuarterStartDate) {
        const formattedNextQuarterDate = new Date(nextSubmissionQuarterStartDate).toLocaleDateString(
          i18n.language === 'fr' ? 'fr-CA' : 'en-US',
          {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          },
        );

        setError(t(serverMessage, { nextQuarterDate: formattedNextQuarterDate }));
      } else if (typeof serverMessage === 'string' && serverMessage.startsWith('submissions.')) {
        setError(t(serverMessage));
      } else {
        setError(t('submissions.createFailed'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setSelectedCriteria(null);
      setError(null);
      setInfoMessage(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
        },
      }}>
      <DialogTitle
        sx={{
          borderBottom: `2px solid ${theme.palette.mode === 'dark' ? '#FFD200' : '#000'}`,
        }}>
        <Box>
          <Typography
            component="div"
            variant="h1"
            sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? '#FFD200' : '#000' }}>
            {t('submissions.createNew')}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, letterSpacing: '-0.02em' }}>
            {t('submissions.selectCriteriaToBegin')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 4 }}>
        {criteriasError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('submissions.criteriaLoadFailed')} {criteriasError.message}
          </Alert>
        )}
        {dealerDetailsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('submissions.dealerLoadFailed')} {dealerDetailsError.message}
          </Alert>
        )}
        {quartersError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('submissions.quarterLoadFailed')} {quartersError.message}
          </Alert>
        )}
        {submissionsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('submissions.submissionLoadFailed')} {submissionsError.message}
          </Alert>
        )}
        {infoMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {infoMessage}
          </Alert>
        )}
        {isLoadingCriterias || isLoadingQuarters || isLoadingDealerSubmissions || isLoadingDealerDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#FFD200' }} />
          </Box>
        ) : normalizedCriterias.length === 0 ? (
          <Alert severity="info">{t('submissions.noCriteriaAvailable')}</Alert>
        ) : filteredCriterias.length === 0 ? (
          <Alert severity={missingDealerCountry ? 'warning' : 'info'}>
            {missingDealerCountry ? t('submissions.noDealerCountry') : t('submissions.noCriteriaForCountry')}
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}>
            {filteredCriterias.map(criteria => (
              <Card
                key={criteria.id}
                onClick={() => handleCriteriaSelect(criteria)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: `2px solid ${selectedCriteria?.id === criteria.id ? '#FFD200' : theme.palette.borderColor.main}`,
                  bgcolor:
                    selectedCriteria?.id === criteria.id
                      ? theme.palette.mode === 'dark'
                        ? 'rgba(255, 210, 0, 0.1)'
                        : 'rgba(212, 175, 55, 0.1)'
                      : theme.palette.background.paper,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#FFD200',
                    boxShadow: theme.shadows[4],
                  },
                }}>
                <CardContent>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 600,
                      color: selectedCriteria?.id === criteria.id ? '#FFD200' : theme.palette.text.primary,
                      mb: 1,
                    }}>
                    {translateLabel(criteria.nameLabel, i18n.language) || criteria.name}
                  </Typography>
                  {criteria.description && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {criteria.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCreate}
          disabled={!selectedCriteria || isCreating || !currentQuarter || isLoadingDealerSubmissions}
          variant="contained"
          sx={{
            fontWeight: 700,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#EFBA29',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              bgcolor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }}>
          {isCreating ? (
            <CircularProgress size={24} sx={{ color: 'inherit' }} />
          ) : (
            <Typography variant="h3">{t('common.create')}</Typography>
          )}
        </Button>
        <Button onClick={handleClose} disabled={isCreating} sx={{ color: theme.palette.text.secondary }}>
          <Typography variant="h5">{t('common.cancel')}</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateSubmissionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialCriteriaId: PropTypes.string,
  criteriaCertificationType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CreateSubmissionDialog;
